function splitNewsArticles(articles = []) {
  const safeArticles = Array.isArray(articles) ? articles : []
  return {
    primary: safeArticles[0] || null,
    secondary: safeArticles.slice(1, 3),
    more: safeArticles.slice(3)
  }
}

function appendUniqueNews(currentArticles = [], incomingArticles = []) {
  const seen = new Set()
  return [...currentArticles, ...incomingArticles].filter((article) => {
    const key = String(article && article.id)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function hasMoreNews({ totalCount, loadedCount, lastPageCount, pageSize }) {
  const total = Number(totalCount) || 0
  if (total > 0) return loadedCount < total
  return Number(lastPageCount) >= Number(pageSize)
}

function decodeHtmlEntities(value) {
  const named = {
    amp: '&',
    apos: "'",
    gt: '>',
    lt: '<',
    nbsp: ' ',
    quot: '"'
  }

  return value.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, entity) => {
    const lower = entity.toLowerCase()
    if (named[lower]) return named[lower]
    if (lower.startsWith('#x')) return String.fromCodePoint(Number.parseInt(lower.slice(2), 16))
    if (lower.startsWith('#')) return String.fromCodePoint(Number.parseInt(lower.slice(1), 10))
    return match
  })
}

function toPlainText(value) {
  return decodeHtmlEntities(String(value || '')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim()
}

function getNewsSummary(article = {}) {
  return toPlainText(article.summary || article.description || article.intro || article.content)
}

function buildNewsDetailPath(urlSlug) {
  const normalized = String(urlSlug || '').trim()
  return normalized ? `/news/${encodeURIComponent(normalized)}` : '/news'
}

function mapHomeFeaturedStories(items = []) {
  return (Array.isArray(items) ? items : []).map((item) => ({
    position: item.position,
    id: item.id,
    urlSlug: item.urlSlug,
    title: item.title,
    coverImage: item.coverImage
  }))
}

function isLegacyNewsId(value) {
  return /^\d+$/.test(String(value || '').trim())
}

function buildAllNewsPath() {
  return '/news/all'
}

function getUnifiedNewsData(response) {
  if (!response || response.code !== 0) {
    throw new Error((response && response.message) || 'The news service returned an invalid response.')
  }
  return response.data
}

function normalizeNewsPageResponse(response) {
  const data = getUnifiedNewsData(response)
  if (!data || !Array.isArray(data.list)) {
    throw new Error('The news service returned an invalid response.')
  }
  return {
    total: Number(data.total) || 0,
    pageIndex: Number(data.pageIndex) || 1,
    pageSize: Number(data.pageSize) || 0,
    totalPages: Number(data.totalPages) || 0,
    hasNext: Boolean(data.hasNext),
    list: data.list
  }
}

function normalizeNewsCategoriesResponse(response) {
  const data = getUnifiedNewsData(response)
  if (!Array.isArray(data)) {
    throw new Error('The news service returned an invalid response.')
  }
  return data.map((category) => ({
    id: String(category.categoryId),
    name: category.categoryName
  }))
}

function normalizeNewsDetailResponse(response) {
  const data = getUnifiedNewsData(response)
  if (!data || !data.id) {
    throw new Error('The news service returned an invalid response.')
  }
  return data
}

function normalizeNewsRichText(content) {
  if (typeof content !== 'string') return ''

  return content.replace(/(\sstyle\s*=\s*)(["'])([\s\S]*?)\2/gi, (match, attributePrefix, quote, styles) => {
    const normalizedStyles = styles.replace(
      /(^|;)\s*color\s*:\s*rgb\(\s*255\s*,\s*255\s*,\s*255\s*\)\s*;?/gi,
      '$1'
    )
    return `${attributePrefix}${quote}${normalizedStyles}${quote}`
  })
}

function createNewsViewTracker() {
  const viewedIds = new Set()
  return (id) => {
    const key = String(id || '')
    if (!key || viewedIds.has(key)) return false
    viewedIds.add(key)
    return true
  }
}

const NEWS_SOCIAL_CHANNELS = [
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'x', label: 'X' }
]

function parseNewsSocialLinks(value) {
  let links
  try {
    links = typeof value === 'string' ? JSON.parse(value) : value
  } catch (error) {
    return []
  }

  if (!links || Array.isArray(links) || typeof links !== 'object') return []

  return NEWS_SOCIAL_CHANNELS.reduce((items, channel) => {
    const href = typeof links[channel.id] === 'string' ? links[channel.id].trim() : ''
    if (!href) return items

    try {
      const url = new URL(href)
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return items
    } catch (error) {
      return items
    }

    items.push({ ...channel, href })
    return items
  }, [])
}

function getTotalPages(totalCount, pageSize) {
  const safePageSize = Math.max(1, Number(pageSize) || 1)
  return Math.max(1, Math.ceil((Number(totalCount) || 0) / safePageSize))
}

function getPaginationItems(currentPage, totalPages) {
  const total = Math.max(1, Number(totalPages) || 1)
  const current = Math.min(total, Math.max(1, Number(currentPage) || 1))
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1)

  const pages = [1]
  if (current > 3) pages.push('ellipsis-left')
  for (let page = Math.max(2, current - 1); page <= Math.min(total - 1, current + 1); page += 1) {
    pages.push(page)
  }
  if (current < total - 2) pages.push('ellipsis-right')
  pages.push(total)
  return pages
}

function shouldUseNewsPlaceholder(coverImage, imageFailed) {
  return !coverImage || Boolean(imageFailed)
}

module.exports = {
  appendUniqueNews,
  buildAllNewsPath,
  buildNewsDetailPath,
  createNewsViewTracker,
  getNewsSummary,
  isLegacyNewsId,
  mapHomeFeaturedStories,
  getPaginationItems,
  getTotalPages,
  hasMoreNews,
  normalizeNewsCategoriesResponse,
  normalizeNewsDetailResponse,
  normalizeNewsPageResponse,
  normalizeNewsRichText,
  parseNewsSocialLinks,
  shouldUseNewsPlaceholder,
  splitNewsArticles,
  toPlainText
}
